import { Spacing } from '@persona-id/design-tokens';
import { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css, CSSObject } from 'styled-components';

import Pdf from 'dashboard/components/Pdf';
import { FileInfo, Image, isPdf } from 'dashboard/components-lib/FileDisplay/FileDisplay';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { DocumentFile } from 'dashboard/models/document';
import useToggle from 'lib/hooks/useToggle';

interface DocumentViewerRendererProps {
  files: DocumentFile[];
  zoomScale: number;
  rotation: number;
  documentFileContainerRef?: MutableRefObject<HTMLDivElement>;
  showMessage: (message: string, messageType: MessageType) => void;
  nullStateMessage?: string;
  nullStateRedirect?: string;
  annotations?: ReactNode[][];
  onFileLoad?: (fileIndex: number, numPages: number) => void;
}

interface DocumentViewerBaseRendererProps {
  file: FileInfo;
  onPageLoad: () => void;
  onFileLoad: (numPages: number) => void;
  annotations?: ReactNode[];
  onError: () => void;
  fileIndex: number;
  zoomScale: number;
  rotate: number;
  rendererWidth: number;
}

interface DocumentViewerImageRendererProps extends DocumentViewerBaseRendererProps {
  loading: boolean;
}

const DocumentViewerRendererContainer = styled('div')`
  position: relative;
  min-width: 500px;
  min-height: 0;
  max-height: 100%;
  scrollbar-gutter: stable;
  overflow: hidden;
  &:hover {
    overflow: auto;
  }
`;

const PdfRendererContainer = styled('div')`
  position: relative;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  &:not(:last-child) {
    margin-bottom: ${Spacing[2]};
  }
`;

const ImageRendererContainer = styled('div')<{ height: number }>`
  ${({ height }) => {
    if (height) {
      return css`
        height: ${height}px;
      `;
    }
  }};
  &:not(:last-child) {
    margin-bottom: ${Spacing[2]};
  }
`;

const MAXIMUM_FILE_WIDTH = 1000;

interface ImageTransformStylingContainerProps {
  zoomScale?: number;
  rotate?: number;
  rendererWidth: number;
  fileWidth: number;
  fileHeight: number;
}

const ImageTransformStylingContainer = styled('div')<ImageTransformStylingContainerProps>`
  width: min(100%, ${MAXIMUM_FILE_WIDTH}px);
  margin-left: auto;
  margin-right: auto;

  // css transformation rules to scale, rotate the image depending on the zoom scale and rotation
  // i recommend playing around with https://developer.mozilla.org/en-US/docs/Web/CSS/transform-origin to understand all the rules.
  // default behavior is to center the image of the renderer container and align it to the top of the image before any transformations were made.
  // translate's after rotating and scaling the image is to align to the top of the image.
  //   e.g. for rotate 90, we have transform: translateX(...) rotate(...) scale(...) translate(50%, -50%)
  //   translate (50%, -50%) is the part to align to the top of the image.
  // when the scaled image is larger than the renderer, then we align the image to the left of the renderer instead of the center
  // for optimal scroll behavior and UX and keep top alignment.
  // to acheive this, we have an additional translateX before any rotate, scale and translate
  // that moves the image the excess left width outside of the renderer amount to the right.
  //   e.g. for rotate 0, we have transform: translateX(Math.max((fileWidth * zoomScale - rendererWidth) / 2, 0)px) scale(...)
  //   translateX(Math.max((fileWidth * zoomScale - rendererWidth) / 2, 0)px) is the part to align to the left of the renderer.
  ${({ rotate, fileHeight, fileWidth, zoomScale, rendererWidth }) => {
    if (rotate == null || rotate === 0) {
      return css`
        transform-origin: top center;
        transform: translateX(
            ${Math.max(
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              (fileWidth * zoomScale - rendererWidth) / 2,
              0,
            )}px
          )
          scale(${zoomScale});
      `;
    }
    const styling: CSSObject = {
      transformOrigin: 'top center',
    };
    if (rotate === 90) {
      styling['transform'] = `translateX(${Math.max(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (fileWidth * zoomScale - rendererWidth) / 2,
        0,
      )}px) rotate(${rotate}deg) scale(${
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (fileWidth / fileHeight) * zoomScale
      }) translate(50%, -50%)`;
    } else if (rotate === 180) {
      styling['transform'] = `translateX(${Math.max(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (fileWidth * zoomScale - rendererWidth) / 2,
        0,
      )}px) rotate(${rotate}deg) scale(${zoomScale}) translateY(-100%)`;
    } else if (rotate === 270) {
      styling['transform'] = `translateX(${Math.max(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (fileWidth * zoomScale - rendererWidth) / 2,
        0,
      )}px) rotate(${rotate}deg) scale(${
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (fileWidth / fileHeight) * zoomScale
      }) translate(-50%, -50%) `;
    }
    return css(styling);
  }}
`;

const DocumentViewerRenderer = ({
  files,
  zoomScale,
  rotation,
  showMessage,
  annotations,
  onFileLoad,
  documentFileContainerRef,
}: DocumentViewerRendererProps) => {
  const fileInfos = files.map((file) => ({
    url: file.url,
    label: file.filename,
    name: file.filename,
    type: file['file-type'],
  }));
  const dispatch = useDispatch();
  const [loading, { disable: stopLoading }] = useToggle(true);
  const [isPageLoadComplete, setIsPageLoadComplete] = useState(true);
  const [rendererWidth, setRendererWidth] = useState(0);

  useEffect(() => {
    if (isPageLoadComplete) {
      stopLoading();
    }
  }, [isPageLoadComplete, stopLoading]);

  window.addEventListener(
    'resize',
    () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setRendererWidth(documentFileContainerRef.current?.clientWidth);
    },
    true,
  );

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setRendererWidth(documentFileContainerRef.current?.clientWidth);
  });

  if (files == null || files.length === 0) {
    return null;
  }

  return (
    <DocumentViewerRendererContainer
      id='document-viewer'
      data-test='document-viewer'
      ref={documentFileContainerRef}
    >
      {fileInfos.map((file, i) => {
        if (isPdf(file)) {
          return (
            <DocumentViewerPdfRenderer
              data-test='document-viewer__file-container'
              key={`document-viewer__file_container--${i}`}
              file={file}
              onFileLoad={(numPages: number) => {
                onFileLoad && onFileLoad(i, numPages);
              }}
              onPageLoad={() => {
                setIsPageLoadComplete(true);
              }}
              onError={() => {
                dispatch(
                  showMessage('Failed to load file. Please try again.', MessageType.Failure),
                );
              }}
              rotate={rotation}
              zoomScale={zoomScale}
              fileIndex={i}
              annotations={annotations && annotations[i]}
              rendererWidth={rendererWidth}
            />
          );
        } else {
          return (
            <DocumentViewerImageRenderer
              data-test='document-viewer__file-container'
              key={`document-viewer__file_container--${i}`}
              file={file}
              loading={loading}
              onFileLoad={(numPages: number) => {
                onFileLoad && onFileLoad(i, numPages);
              }}
              onPageLoad={() => {
                setIsPageLoadComplete(true);
              }}
              onError={() => {
                dispatch(
                  showMessage('Failed to load file. Please try again.', MessageType.Failure),
                );
              }}
              rotate={rotation}
              zoomScale={zoomScale}
              fileIndex={i}
              annotations={annotations && annotations[i]}
              rendererWidth={rendererWidth}
            />
          );
        }
      })}
    </DocumentViewerRendererContainer>
  );
};

const DocumentViewerImageRenderer = ({
  file,
  loading,
  onFileLoad,
  onPageLoad,
  onError,
  annotations,
  fileIndex,
  zoomScale,
  rotate,
  rendererWidth,
}: DocumentViewerImageRendererProps) => {
  const imageRef = useRef(null);
  const [imageHeight, imageWidth] = [
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    imageRef?.current?.clientHeight,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    imageRef?.current?.clientWidth,
  ];
  const transformedImageHeight =
    imageHeight &&
    imageWidth &&
    (rotate % 180 === 90 ? imageWidth * (imageWidth / imageHeight) : imageHeight) * zoomScale;

  return (
    <ImageRendererContainer key={`${file.name}-${fileIndex}`} height={transformedImageHeight}>
      <ImageTransformStylingContainer
        zoomScale={zoomScale}
        rotate={rotate}
        fileHeight={imageHeight}
        fileWidth={imageWidth}
        rendererWidth={rendererWidth}
      >
        <Image
          data-test='document-viewer__file-container'
          src={file.url}
          isLoaded={!loading}
          onLoad={() => {
            onFileLoad(/* numPages */ 1);
            onPageLoad && onPageLoad();
          }}
          onError={onError}
          background='primary'
          position='relative'
          display='block'
          ref={imageRef}
        />
        {annotations}
      </ImageTransformStylingContainer>
    </ImageRendererContainer>
  );
};

const DocumentViewerPdfRenderer = ({
  file,
  onFileLoad,
  onPageLoad,
  onError,
  annotations,
  fileIndex,
  rotate,
  zoomScale,
  rendererWidth,
}: DocumentViewerBaseRendererProps) => {
  return (
    <PdfRendererContainer key={`${file.name}-${fileIndex}`}>
      <Pdf
        src={file.url}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onFileLoad={onFileLoad}
        onPageLoad={onPageLoad}
        onError={onError}
        width={Math.min(rendererWidth, MAXIMUM_FILE_WIDTH)}
        background='primary'
        renderPdfTextLayer={false}
        renderAnnotationLayer={false}
        rotate={rotate}
        scale={zoomScale}
        annotations={annotations}
        loading={<LoadingIndicator fullscreen />}
      />
    </PdfRendererContainer>
  );
};

export default DocumentViewerRenderer;
